import {registerGlobalInitFunc} from '../modules/observer.ts';
import {showErrorToast} from '../modules/toast.ts';
import {initAllSpiceMarkdownContent} from '../markup/content.ts';
import {initMarkupAnchorsIn} from '../markup/anchors.ts';
import {applyAreYouSure} from '../vendor/jquery.are-you-sure.ts';
import {confirmModal} from './comp/ConfirmModal.ts';
import {attachRefIssueContextPopup} from './contextpopup.ts';
import {initRepoIssueContentHistory} from './repo-issue-content.ts';
import {fetchDesignReviewPages, type DesignReviewPage} from './design-review-pages.ts';
import {highlightFilterMatches} from './repo-issue-comment-filter-highlight.ts';

type HtmxConfigRequestEvent = CustomEvent<{parameters: FormData}>;
type HtmxConfirmEvent = CustomEvent<{issueRequest: (skipConfirmation: boolean) => void, triggeringEvent?: Event}>;
type HtmxBeforeSwapEvent = CustomEvent<{requestConfig: {elt: Element}, shouldSwap: boolean, serverResponse: string}>;

const timelineId = 'issue-timeline-comments';
const timelineSelector = `#${timelineId}`;

function filterFields(form: HTMLFormElement): HTMLInputElement[] {
  return Array.from(form.querySelectorAll<HTMLInputElement>('input[name]'));
}

// The state of a field that tells whether the reader changed it.
function fieldState(field: HTMLInputElement): string {
  return field.type === 'checkbox' ? String(field.checked) : field.value;
}

/** Returns the form's non-empty fields as a query string with the keys in a stable order. */
export function filterQuery(form: HTMLFormElement): string {
  const params = new URLSearchParams();
  for (const [key, value] of new FormData(form)) {
    if (value !== '') params.append(key, value as string);
  }
  params.sort();
  return params.toString();
}

export function hasFilterValue(form: HTMLFormElement): boolean {
  return filterFields(form).some((field) => (field.type === 'checkbox' ? field.checked : field.value !== ''));
}

export function updateChecklistCounts(form: HTMLFormElement) {
  for (const checklist of form.querySelectorAll('.issue-comment-filter-checklist')) {
    const count = checklist.querySelector('.issue-comment-filter-checklist-count');
    if (!count) continue;
    const selected = checklist.querySelectorAll('input[type=checkbox]:checked').length;
    count.textContent = String(selected);
    count.classList.toggle('tw:hidden', selected === 0);
  }
}

/**
 * Sets the fields to the applied filter. It assigns only a different value,
 * because assigning a date input's own value can erase a date the reader is
 * typing. It skips the fields that `keep` returns true for.
 */
export function showAppliedFilter(form: HTMLFormElement, applied: URLSearchParams, keep: (field: HTMLInputElement) => boolean = () => false) {
  for (const field of filterFields(form)) {
    if (keep(field)) continue;
    if (field.type === 'checkbox') {
      const checked = applied.getAll(field.name).includes(field.value);
      if (field.checked !== checked) field.checked = checked;
    } else {
      const value = applied.get(field.name) ?? '';
      if (field.value !== value) field.value = value;
    }
  }
  updateChecklistCounts(form);
}

export function clearFilterFields(form: HTMLFormElement) {
  showAppliedFilter(form, new URLSearchParams());
}

/**
 * Replaces the fallback label of each multi-page page option with the page
 * name. A single-page file is its own page, so its path stays the label.
 */
export function labelPageOptions(form: HTMLFormElement, pages: DesignReviewPage[]) {
  for (const label of form.querySelectorAll<HTMLElement>('[data-page-path][data-page-id]:not([data-page-id=""])')) {
    const path = label.getAttribute('data-page-path');
    const page = pages.find((p) => p.headPath === path && p.pageId === label.getAttribute('data-page-id'));
    if (!page) continue;
    label.textContent = `${page.name} · ${path.split('/').pop()}`;
    label.closest('label')?.setAttribute('title', `${path} · ${page.name}`);
  }
}

// Uses the same rule as the "unsaved changes" warning when the reader leaves the page:
// a form with the "dirty" class that is not hidden.
function hasUnsavedEdits(): boolean {
  const forms = document.querySelectorAll(`${timelineSelector} form.dirty:not(.ignore-dirty)`);
  return Array.from(forms).some((form) => !form.closest('.tw\\:hidden'));
}

function appliedFilter(timeline: Element | null): URLSearchParams {
  return new URLSearchParams(timeline?.getAttribute('data-comment-filter-applied') ?? '');
}

// Sets up the parts of a new timeline that only run once on page load.
function initNewTimeline(timeline: HTMLElement) {
  initAllSpiceMarkdownContent(timeline); // schematic and PCB snippets
  attachRefIssueContextPopup(timeline.querySelectorAll('.ref-issue')); // issue reference popups
  initMarkupAnchorsIn(timeline); // heading anchors and in-page links
  applyAreYouSure(`${timelineSelector} form:not(.ignore-dirty)`); // unsaved-changes tracking for reply forms
  initRepoIssueContentHistory(); // the "edited" menu on each comment
}

// Loads the page names the first time the reader opens the page list, because
// it asks crysknife for every schematic file in the Design Review.
function initPageNames(form: HTMLFormElement) {
  const pageList = form.querySelector<HTMLDetailsElement>('details[data-name="page"]');
  if (!pageList) return;
  const onToggle = async () => {
    if (!pageList.open) return;
    pageList.removeEventListener('toggle', onToggle);
    try {
      const pages = await fetchDesignReviewPages(form.getAttribute('data-repo-owner'), form.getAttribute('data-repo-name'), form.getAttribute('data-issue-index'));
      labelPageOptions(form, pages);
    } catch (err) {
      console.warn('failed to load the page names for the comment filter', err);
    }
  };
  pageList.addEventListener('toggle', onToggle);
}

// The Design Review comment filter form. HTMX sends the request and replaces
// #issue-timeline-comments (see templates/repo/issue/view_content/comment_filter.tmpl).
// This adds the parts that the HTMX attributes cannot do.
export function initRepoIssueCommentFilter() {
  registerGlobalInitFunc('initRepoIssueCommentFilter', (form: HTMLFormElement) => {
    const status = form.querySelector('#issue-comment-filter-status');
    // The field states and query of the last request, to tell whether the
    // reader changed a field after sending it, and to skip a repeated request.
    let sentStates = new Map(filterFields(form).map((field) => [field, fieldState(field)]));
    let sentQuery: string | null = filterQuery(form);
    let confirming = false;

    const showTimelineFilter = () => showAppliedFilter(form, appliedFilter(document.querySelector(timelineSelector)));

    form.addEventListener('change', () => updateChecklistCounts(form));
    initPageNames(form);
    const initialTimeline = document.querySelector(timelineSelector);
    highlightFilterMatches(initialTimeline, appliedFilter(initialTimeline));

    // Leave empty fields out of the request, so the pushed URL only has the active filters.
    form.addEventListener('htmx:configRequest', (e) => {
      sentStates = new Map(filterFields(form).map((field) => [field, fieldState(field)]));
      sentQuery = filterQuery(form);
      const params = (e as HtmxConfigRequestEvent).detail.parameters;
      for (const [key, value] of Array.from(params.entries())) {
        if (value === '') params.delete(key);
      }
      document.querySelector(timelineSelector)?.setAttribute('aria-busy', 'true');
    });

    form.addEventListener('htmx:confirm', async (e) => {
      const detail = (e as HtmxConfirmEvent).detail;
      const trigger = detail.triggeringEvent?.type;
      // Typing and then leaving the search box fires both "input" and "change"
      // for the same text. A submit always runs, so Clear can tidy the URL.
      if (trigger !== 'submit' && filterQuery(form) === sentQuery) {
        e.preventDefault();
        return;
      }
      if (!hasUnsavedEdits()) return;

      // Replacing the timeline removes any reply or edit that the reader has not saved.
      // While the reader types, wait for them to leave the box or press Enter before asking.
      e.preventDefault();
      if (trigger === 'input' || confirming) return;
      confirming = true;
      try {
        if (await confirmModal({content: form.getAttribute('data-unsaved-confirm'), confirmButtonColor: 'red'})) {
          detail.issueRequest(true);
        } else {
          showTimelineFilter();
        }
      } finally {
        confirming = false;
      }
    });

    // When a request fails, the timeline keeps the old filter, so the fields must show it too.
    // htmx.ts already shows a toast for these errors.
    for (const event of ['htmx:responseError', 'htmx:sendError']) {
      form.addEventListener(event, () => {
        sentQuery = null;
        showTimelineFilter();
      });
    }
    // htmx:afterRequest fires after every request, whether it swapped the timeline or not.
    form.addEventListener('htmx:afterRequest', () => {
      document.querySelector(timelineSelector)?.removeAttribute('aria-busy');
    });

    // A successful response without the timeline, such as a sign-in or single sign-on page,
    // would remove the timeline. Keep the old timeline and URL instead.
    document.addEventListener('htmx:beforeSwap', (e) => {
      const detail = (e as HtmxBeforeSwapEvent).detail;
      if (detail.requestConfig.elt !== form || !detail.shouldSwap) return;
      if (detail.serverResponse.includes(`id="${timelineId}"`)) return;
      detail.shouldSwap = false;
      sentQuery = null;
      showErrorToast(form.getAttribute('data-load-failed'));
      showTimelineFilter();
    });

    form.querySelector('.issue-comment-filter-clear').addEventListener('click', () => {
      // Send a request even when the fields are empty if the URL still has a filter parameter,
      // for example a date that the server ignored, so the URL is cleaned up too.
      const urlParams = new URLSearchParams(window.location.search);
      const urlHasFilter = filterFields(form).some((field) => urlParams.has(field.name));
      if (!hasFilterValue(form) && !urlHasFilter) return;
      clearFilterFields(form);
      form.requestSubmit();
    });

    // HTMX fires htmx:afterSettle on the new element, not on the form.
    document.addEventListener('htmx:afterSettle', (e) => {
      const timeline = e.target as HTMLElement;
      if (timeline.id !== timelineId) return;

      // Show the filter that the server applied, for example without a date it could not read.
      // Skip a field that the reader changed after this request was sent.
      const applied = appliedFilter(timeline);
      showAppliedFilter(form, applied, (field) => sentStates.get(field) !== fieldState(field));
      applied.sort();
      sentQuery = applied.toString();
      status.textContent = document.querySelector('#issue-comment-filter-count')?.getAttribute('data-tooltip-content') ?? '';
      initNewTimeline(timeline);
      highlightFilterMatches(timeline, appliedFilter(timeline));
    });
  });
}
